import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Course } from '../types';
import { programImages } from '../data/programImages';

interface CourseContextType {
  courses: Course[];
  addCourse: (course: Omit<Course, 'id' | 'createdAt' | 'updatedAt' | 'currentApplicants'>) => void;
  updateCourse: (id: string, course: Partial<Course>) => void;
  deleteCourse: (id: string) => void;
  getCourse: (id: string) => Course | undefined;
}

const CourseContext = createContext<CourseContextType | undefined>(undefined);

export const CourseProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [courses, setCourses] = useState<Course[]>(() => {
    // 이미지 업데이트를 위해 localStorage 초기화 (한 번만 실행)
    const needsImageUpdate = localStorage.getItem('coursesImageUpdated') !== 'true';
    if (needsImageUpdate) {
      localStorage.removeItem('courses');
      localStorage.setItem('coursesImageUpdated', 'true');
    }

    // localStorage에서 데이터 불러오기
    const savedCourses = localStorage.getItem('courses');
    if (savedCourses && !needsImageUpdate) {
      return JSON.parse(savedCourses);
    }

    // 초기 샘플 데이터
    return [
      {
        id: '1',
        courseName: '비즈니스 영어회화',
        instructor: '김영희',
        status: 'recruiting',
        registrationStartDate: '2024-12-01',
        registrationEndDate: '2024-12-10',
        registrationStartTime: '09:00',
        registrationEndTime: '18:00',
        courseStartDate: '2024-12-15',
        courseEndDate: '2025-02-15',
        currentApplicants: 15,
        maxStudents: 20,
        waitingList: 5,
        tuition: 150000,
        materialFee: 0,
        selectionMethod: 'first-come',
        courseDescription: '비즈니스 상황에서 필요한 실용 영어회화를 학습합니다.',
        courseCycle: 'weekly',
        courseDays: ['월', '수', '금'],
        courseTime: '19:00-21:00',
        totalSessions: 36,
        institution: '동두천 글로벌인재센터',
        location: '3층 301호',
        contactPerson: '교육담당자',
        contactPhone: '031-123-4567',
        courseImage: programImages['1']
      },
      {
        id: '2',
        courseName: '토익 집중반',
        instructor: '이민수',
        status: 'ongoing',
        registrationStartDate: '2024-11-01',
        registrationEndDate: '2024-11-10',
        registrationStartTime: '09:00',
        registrationEndTime: '18:00',
        courseStartDate: '2024-11-15',
        courseEndDate: '2025-01-15',
        currentApplicants: 25,
        maxStudents: 25,
        waitingList: 10,
        tuition: 200000,
        materialFee: 30000,
        selectionMethod: 'first-come',
        courseDescription: 'TOEIC 고득점을 목표로 하는 집중 과정입니다.',
        courseCycle: 'weekly',
        courseDays: ['화', '목'],
        courseTime: '18:00-20:00',
        totalSessions: 24,
        institution: '동두천 글로벌인재센터',
        location: '4층 401호',
        contactPerson: '교육담당자',
        contactPhone: '031-123-4567',
        courseImage: programImages['2']
      }
    ];
  });

  // 데이터 변경 시 localStorage에 저장
  useEffect(() => {
    localStorage.setItem('courses', JSON.stringify(courses));
  }, [courses]);

  const addCourse = (courseData: Omit<Course, 'id' | 'createdAt' | 'updatedAt' | 'currentApplicants'>) => {
    const newCourse: Course = {
      ...courseData,
      id: Date.now().toString(),
      currentApplicants: 0,
      status: 'recruiting',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setCourses(prev => [...prev, newCourse]);
  };

  const updateCourse = (id: string, courseData: Partial<Course>) => {
    setCourses(prev => prev.map(course =>
      course.id === id
        ? { ...course, ...courseData, updatedAt: new Date().toISOString() }
        : course
    ));
  };

  const deleteCourse = (id: string) => {
    setCourses(prev => prev.filter(course => course.id !== id));
  };

  const getCourse = (id: string) => {
    return courses.find(course => course.id === id);
  };

  return (
    <CourseContext.Provider value={{ courses, addCourse, updateCourse, deleteCourse, getCourse }}>
      {children}
    </CourseContext.Provider>
  );
};

export const useCourses = () => {
  const context = useContext(CourseContext);
  if (context === undefined) {
    throw new Error('useCourses must be used within a CourseProvider');
  }
  return context;
};